import ModeBadge from '@/components/console/mode-badge';
import EmptyState from '@/components/empty-state';
import PageHeader from '@/components/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { formatNumber } from '@/lib/format';
import DraftCard from '@/pages/apps/drafts/components/draft-card';
import { app_drafts_path } from '@/rails/routes';
import type { AppShell, Draft, ProviderMode } from '@/types/platform';
import { Form, router, usePage } from '@inertiajs/react';
import { useEffect } from 'react';

interface BuildCredits {
  used: number;
  limit: number | null;
  remaining: number | null;
}

interface Props {
  app: AppShell;
  drafts: Draft[];
  generating: boolean;
  engineMode: ProviderMode;
  buildCredits: BuildCredits;
}

const POLL_EVERY_MS = 2500;
const MAX_PROMPT_LENGTH = 4000;

const ENGINE_NOTES: Record<ProviderMode, string> = {
  live: 'Drafted by a free model. You review every draft before it changes anything.',
  demo: 'Describing an app is not switched on yet, so each draft is the closest template. To pick one yourself, start a new app from a template.',
  unconfigured: 'Drafting is not connected yet.',
};
// Only staff can connect the drafting model, so only staff are told to.
const STAFF_DEMO_NOTE =
  'Demo mode: drafts are matched to templates by keyword. Connect OpenRouter for model-written drafts.';

function creditsLine(credits: BuildCredits): string {
  if (credits.limit === null) {
    return `${formatNumber(credits.used)} build credits used this month. Your plan does not cap them.`;
  }

  return `${formatNumber(credits.used)} of ${formatNumber(credits.limit)} build credits used this month. ${formatNumber(credits.remaining)} left.`;
}

const Index = ({ app, drafts, generating, engineMode, buildCredits }: Props) => {
  const { staff } = usePage<{ staff?: boolean }>().props;

  // A draft is written in the background. Ask again until none is still being written.
  useEffect(() => {
    if (!generating) return undefined;

    const timer = window.setInterval(() => {
      router.reload({ only: ['drafts', 'generating', 'buildCredits'] });
    }, POLL_EVERY_MS);

    return () => window.clearInterval(timer);
  }, [generating]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Design with AI"
        description="Describe a change in your own words. You get a draft to review; nothing changes until you accept it."
        badges={engineMode === 'live' ? null : <ModeBadge mode={engineMode} />}
      />

      <Card>
        <CardContent>
          <Form
            action={app_drafts_path(app.id)}
            method="post"
            resetOnSuccess
            options={{ preserveScroll: true }}
            className="space-y-3"
          >
            {({ processing }) => (
              <>
                <Label htmlFor="draft-prompt">Describe what to add or change</Label>
                <Textarea
                  id="draft-prompt"
                  name="draft[prompt]"
                  rows={4}
                  required
                  maxLength={MAX_PROMPT_LENGTH}
                  aria-describedby="draft-prompt-notes"
                  placeholder="Add a list of equipment with a name, a serial number, and the date it was last serviced."
                />
                <div id="draft-prompt-notes" className="text-muted-foreground space-y-1 text-sm">
                  <p>
                    {staff && engineMode === 'demo' ? STAFF_DEMO_NOTE : ENGINE_NOTES[engineMode]}
                  </p>
                  <p>Each draft uses one build credit. {creditsLine(buildCredits)}</p>
                </div>
                <Button type="submit" disabled={processing || engineMode === 'unconfigured'}>
                  {processing ? 'Sending…' : 'Draft it'}
                </Button>
              </>
            )}
          </Form>
        </CardContent>
      </Card>

      <section aria-labelledby="drafts-heading" className="space-y-4">
        <h2 id="drafts-heading" className="text-lg font-semibold">
          Drafts
        </h2>
        {drafts.length === 0 ? (
          <Card>
            <CardContent>
              <EmptyState description="No drafts yet. Describe a change above and the first one shows up here." />
            </CardContent>
          </Card>
        ) : (
          drafts.map((draft) => <DraftCard key={draft.id} app={app} draft={draft} />)
        )}
      </section>
    </div>
  );
};

export default Index;
